export type BlockAlignment = 'left' | 'center' | 'right' | 'justify';

// A block-level marker in our own minimal markdown dialect (not real CommonMark) that carries a
// paragraph/heading's text alignment through the plain-string storage format. Anchored to the
// very start of a block on its own line, so it can't collide with a genuine markdown link like
// "[align:foo](bar)" (which has a parenthesized destination immediately after).
const ALIGN_PREFIX_REGEX = /^\[align:(center|right|justify)\]\n([\s\S]*)$/;

// Strips a leading `[align:X]` marker (if present) from a block, returning the alignment (or
// 'left' if none) and the remaining block text. Shared by the Preview renderer (SchedulePrint.tsx)
// and the PDF renderer (markdownToPdf.tsx) so both agree on the format.
export const extractBlockAlignment = (block: string): { align: BlockAlignment; text: string } => {
  const match = block.match(ALIGN_PREFIX_REGEX);
  if (!match) return { align: 'left', text: block };
  return { align: match[1] as BlockAlignment, text: match[2] };
};

const HEADING_LINE_REGEX = /^#{1,3}\s+/;
const ALIGN_LINE_REGEX = /^\[align:(center|right|justify)\]$/;

// Splits raw markdown into logical blocks, scanning line by line (not by blank-line-delimited
// chunks) so that:
//  - a #/##/### heading line or a lone "---" divider line is ALWAYS its own block, even when it
//    wasn't separated from surrounding text by a blank line — matches how people naturally type
//    (single Enter between every line) rather than requiring the stricter CommonMark convention
//    of a blank line before/after a heading.
//  - a single blank line is markdown's ordinary block SEPARATOR and produces no block of its
//    own, but each ADDITIONAL consecutive blank line becomes an explicit empty block (an empty
//    paragraph) — i.e. a run of n blank lines yields n-1 empty blocks, so intentional extra
//    spacing an org admin typed renders as visible gaps rather than being silently collapsed.
//  - a `[align:X]` marker line applies to whichever block comes right after it.
// Shared by the Preview renderer (SchedulePrint.tsx) and the PDF renderer (markdownToPdf.tsx) so
// both agree on block boundaries.
export const splitMarkdownIntoBlocks = (markdown: string): string[] => {
  // Only edge whitespace is trimmed — interior blank lines are preserved by the per-line scan
  // below, which is the whole point of this rewrite.
  const trimmed = markdown.replace(/^[ \t]*\n+/, '').replace(/\n+[ \t]*$/, '');
  if (!trimmed) return [];

  const lines = trimmed.split('\n');
  const blocks: string[] = [];
  let buffer: string[] = [];
  let pendingAlign: BlockAlignment | null = null;
  let blankRun = 0;

  const withPendingAlign = (blockText: string): string => {
    if (!pendingAlign) return blockText;
    const prefixed = `[align:${pendingAlign}]\n${blockText}`;
    pendingAlign = null;
    return prefixed;
  };

  const flush = () => {
    if (buffer.length === 0) return;
    blocks.push(withPendingAlign(buffer.join('\n')));
    buffer = [];
  };

  // The first blank line of a run is just the separator between the surrounding blocks; each
  // additional one is an intentional empty paragraph the org admin typed.
  const flushBlankRun = () => {
    for (let i = 1; i < blankRun; i++) {
      blocks.push('');
    }
    blankRun = 0;
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (trimmedLine === '') {
      flush();
      blankRun += 1;
      continue;
    }

    flushBlankRun();

    const alignMatch = trimmedLine.match(ALIGN_LINE_REGEX);
    if (alignMatch) {
      flush();
      pendingAlign = alignMatch[1] as BlockAlignment;
      continue;
    }

    const isStructuralLine = HEADING_LINE_REGEX.test(trimmedLine) || trimmedLine === '---';
    if (isStructuralLine) {
      flush();
      blocks.push(withPendingAlign(trimmedLine));
    } else {
      buffer.push(line);
    }
  }
  flush();

  return blocks;
};
